import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppState } from 'redux/config/store'
import { reimbursementsActions } from 'redux/reimbursements/reimbursementsSlice'
import Text from 'core-system/Text'
import AlertBanner from 'core-system/AlertBanner'
import { uploadReceiptForReimbursement } from './uploadFile'
import FleetCardModal from './components/FleetCardModal'
import { EmployeeLocations } from 'shared/Router/EmployeeLocations'
import styled from 'styled-components'
import GridCard from 'employee-platform/shared/components/GridCard'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import ReimbursementsTable, {
  ReimbursementRowData,
} from 'features/Reimbursements/components/shared/ReimbursementsTable'
import SubmitReimbursementCard from 'features/Reimbursements/components/shared/SubmitReimbursementCard'
import GuidelinesCard from 'features/Reimbursements/components/shared/GuidelinesCard'
import ReceiptReviewModal, {
  ProcessedReceipt,
} from 'features/Reimbursements/components/shared/ReceiptReviewModal'

const Container = styled.div`
  padding: 2rem;
`

const TableSection = styled.div`
  margin-top: 2rem;
`

const TableHeader = styled(Text)`
  font-weight: 600;
  margin-bottom: 1rem;
`

const ReimbursementView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { mySubmissions, loading } = useSelector(
    (state: AppState) => state.reimbursements
  )

  const [showAlertBanner, setShowAlertBanner] = useState(false)
  const [showErrorBanner, setShowErrorBanner] = useState(false)
  const [showFleetCardModal, setShowFleetCardModal] = useState(true)
  const [showReceiptReviewModal, setShowReceiptReviewModal] = useState(false)
  const [processedReceipts, setProcessedReceipts] = useState<
    ProcessedReceipt[]
  >([])
  const [receiptDownloadUrl, setReceiptDownloadUrl] = useState('')
  const [receiptContentType, setReceiptContentType] = useState('')

  useEffect(() => {
    dispatch(reimbursementsActions.getMySubmissions())
  }, [dispatch])

  const handleUpload = async (files: File[]) => {
    const response = await uploadReceiptForReimbursement(files[0])
    if (response.status === 'success' && 'receipts' in response) {
      setShowErrorBanner(false)
      setProcessedReceipts(response.receipts)
      setReceiptDownloadUrl(response.downloadUrl)
      setReceiptContentType(response.contentType)
      setShowReceiptReviewModal(true)
    } else {
      setShowAlertBanner(false)
      setShowErrorBanner(true)
    }
  }

  const tableData: ReimbursementRowData[] | null = mySubmissions
    ? mySubmissions.map((r) => ({
        id: r.id,
        receiptDate: r.receiptDatetime,
        submittedAt: r.createdAt,
        amount: r.amount,
        approvedAmount: r.approvedAmount,
        status: r.status,
        expenseType: r.expenseType,
        note: r.note,
        receiptUrl: r.receiptUrl ?? null,
        merchantCurrency: r.merchantCurrency ?? null,
      }))
    : null

  return (
    <Container>
      {showAlertBanner && (
        <AlertBanner
          variant='success'
          message='Your claims were created successfully!'
          closeFn={() => setShowAlertBanner(false)}
          marginBottom='1rem'
        />
      )}
      {showErrorBanner && (
        <AlertBanner
          variant='redAlert'
          message='There was an issue uploading your receipt. Make sure this is an accepted receipt type and that you have not previously uploaded this receipt for reimbursement.'
          closeFn={() => setShowErrorBanner(false)}
          marginBottom='1rem'
        />
      )}
      <GridContainer numColumns={2} fitContentHeight>
        <GridCard headerTitle='Submit Reimbursement'>
          <SubmitReimbursementCard onUpload={handleUpload} />
        </GridCard>

        <GridCard headerTitle='Reimbursement Guidelines'>
          <GuidelinesCard />
        </GridCard>
      </GridContainer>

      <TableSection>
        <TableHeader variant='h5'>Your Reimbursements</TableHeader>
        <ReimbursementsTable
          data={tableData}
          loading={loading}
          variant='employee'
          emptyMessage='No Reimbursements Found'
          emptySubtitle='Upload a receipt to submit your first reimbursement'
        />
      </TableSection>

      <FleetCardModal
        open={showFleetCardModal}
        onClose={() => setShowFleetCardModal(false)}
        onGetCard={() => navigate(EmployeeLocations.EmployeeSettings)}
        onContinue={() => setShowFleetCardModal(false)}
      />

      <ReceiptReviewModal
        open={showReceiptReviewModal}
        onClose={() => setShowReceiptReviewModal(false)}
        receipts={processedReceipts}
        variant='employee'
        downloadUrl={receiptDownloadUrl}
        contentType={receiptContentType}
        onSubmitSuccess={() => {
          setShowAlertBanner(true)
          setShowErrorBanner(false)
          dispatch(reimbursementsActions.getMySubmissions())
        }}
        onSubmitError={() => {
          setShowAlertBanner(false)
          setShowErrorBanner(true)
        }}
      />
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ReimbursementView.displayName = 'ReimbursementView'
}

export default ReimbursementView
